/**
 * Préfixe URL du déploiement (sous-dossier), injecté au build par CRA (PUBLIC_URL / homepage).
 * Ex. `/opsecours` pour https://hôte/opsecours/ — laisser vide pour la racine du site.
 */
export const getPublicPathPrefix = (): string => {
  const pub = (process.env.PUBLIC_URL || '').trim();
  if (!pub || pub === '.' || !pub.startsWith('/')) return '';
  return pub.replace(/\/$/, '');
};

/** basename React Router (undefined = racine). */
export const getRouterBasename = (): string | undefined => {
  const p = getPublicPathPrefix();
  return p || undefined;
};

/** Chemin Socket.IO côté navigateur (doit correspondre aux règles du .htaccess). */
export const getSocketIoPath = (): string => {
  const p = getPublicPathPrefix();
  return p ? `${p}/socket.io` : '/socket.io';
};

// Détecter si on est en mode Electron
const isElectron = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Vérifier via l'API Electron
  const electronAPI = (window as any).electronAPI;
  if (electronAPI?.isElectron && electronAPI.isElectron()) {
    return true;
  }
  
  // Vérifier via user agent
  if (navigator.userAgent.toLowerCase().includes('electron')) {
    return true;
  }
  
  return false;
};

// Configuration API
export const getApiBaseUrl = (includeApiPath: boolean = false): string => {
  // En mode Electron, toujours utiliser localhost
  if (isElectron()) {
    return includeApiPath ? 'http://localhost:3001/api' : 'http://localhost:3001';
  }
  
  if (process.env.REACT_APP_API_URL) {
    const baseUrl = process.env.REACT_APP_API_URL;
    if (includeApiPath && !baseUrl.endsWith('/api')) {
      return baseUrl.endsWith('/') ? `${baseUrl}api` : `${baseUrl}/api`;
    }
    return baseUrl;
  }
  
  if (process.env.NODE_ENV === 'production') {
    const prefix = getPublicPathPrefix();
    if (includeApiPath) {
      return prefix ? `${prefix}/api` : '/api';
    }
    return prefix;
  }
  
  return includeApiPath ? 'http://localhost:3001/api' : 'http://localhost:3001';
};

export const API_CONFIG = {
  baseUrl: getApiBaseUrl(),
  timeout: 10000,
  retries: 3,
  retryDelay: 1000
};

export const API_ENDPOINTS = {
  auth: {
    login: '/api/auth/login',
    logout: '/api/auth/logout',
    register: '/api/auth/register',
    refresh: '/api/auth/refresh',
    profile: '/api/auth/profile'
  },
  buildings: {
    list: '/api/buildings',
    get: (id: number) => `/api/buildings/${id}`,
    create: '/api/buildings',
    update: (id: number) => `/api/buildings/${id}`,
    delete: (id: number) => `/api/buildings/${id}`,
    transfer: (id: number) => `/api/buildings/${id}/transfer`,
    refund: (id: number) => `/api/buildings/${id}/refund`
  },
  vehicles: {
    list: '/api/vehicles',
    get: (id: number) => `/api/vehicles/${id}`,
    create: '/api/vehicles',
    update: (id: number) => `/api/vehicles/${id}`,
    transfer: (id: number) => `/api/vehicles/${id}/transfer`
  },
  personnel: {
    list: '/api/personnel',
    get: (id: number) => `/api/personnel/${id}`,
    create: '/api/personnel',
    update: (id: number) => `/api/personnel/${id}`,
    transfer: (id: number) => `/api/personnel/${id}/transfer`,
    retire: (id: number) => `/api/personnel/${id}/retire`
  },
  interventions: {
    list: '/api/interventions',
    get: (id: number) => `/api/interventions/${id}`,
    create: '/api/interventions',
    update: (id: number) => `/api/interventions/${id}`,
    assign: (id: number) => `/api/interventions/${id}/assign`,
    complete: (id: number) => `/api/interventions/${id}/complete`
  },
  calls: {
    list: '/api/calls',
    take: (id: number) => `/api/calls/${id}/take`,
    end: (id: number) => `/api/calls/${id}/end`
  },
  game: {
    state: '/api/game/state',
    activate: '/api/game/activate',
    mode: '/api/game/mode'
  },
  dashboard: {
    stats: '/api/dashboard/stats'
  }
};

export const API_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json'
};

export const getAuthHeaders = (token: string) => ({
  ...API_HEADERS,
  'Authorization': `Bearer ${token}`
});

export const API_ERRORS = {
  NETWORK_ERROR: 'Erreur de connexion réseau',
  TIMEOUT_ERROR: 'Délai d\'attente dépassé',
  UNAUTHORIZED: 'Non autorisé',
  FORBIDDEN: 'Accès interdit',
  NOT_FOUND: 'Ressource non trouvée',
  SERVER_ERROR: 'Erreur serveur',
  VALIDATION_ERROR: 'Erreur de validation',
  UNKNOWN_ERROR: 'Erreur inconnue'
};

export const handleApiError = (error: any): string => {
  if (error.name === 'AbortError') {
    return API_ERRORS.TIMEOUT_ERROR;
  }
  
  if (error.message?.includes('Network Error')) {
    return API_ERRORS.NETWORK_ERROR;
  }
  
  if (error.response) {
    switch (error.response.status) {
      case 401:
        return API_ERRORS.UNAUTHORIZED;
      case 403:
        return API_ERRORS.FORBIDDEN;
      case 404:
        return API_ERRORS.NOT_FOUND;
      case 422:
        return API_ERRORS.VALIDATION_ERROR;
      case 500:
        return API_ERRORS.SERVER_ERROR;
      default:
        return error.response.data?.message || API_ERRORS.UNKNOWN_ERROR;
    }
  }
  
  return error.message || API_ERRORS.UNKNOWN_ERROR;
};

